/**
 * Reusable sheet-selection dropdown for Daily Photo List.
 *
 * Renders a trigger button that opens a dropdown with:
 *   - Select All checkbox (tri-state)
 *   - Scrollable sheet list with checkboxes + reorder arrows
 *   - "Save as my default" link
 *   - Cancel + configurable action button
 *
 * Used by both Export (Download XLSX) and Print workflows.
 */
import React, { useCallback, useEffect, useRef, useState } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"

import { MOBILE_FULL_BUTTON } from "../../config/mobileStyles"
import { THEME } from "../../config/theme"
import { PHOTO_LIST_SHEETS, usePhotoList } from "../../context/PhotoListContext"

const ALL_SHEET_LABELS = PHOTO_LIST_SHEETS.map((s) => s.label)

interface Props {
  /** Label on the trigger button (e.g. "Export" or "Print"). Trailing
   * ChevronDown is rendered automatically, no need to include it here. */
  triggerLabel: string
  /** Optional icon rendered before the trigger label (e.g. Download, Printer) */
  leadingIcon?: React.ReactNode
  /** Label shown while the action is running (e.g. "Preparing..." or "Printing...") */
  busyLabel: string
  /** Label on the primary action button inside the dropdown (e.g. "Download XLSX" or "Print") */
  actionLabel: string
  /** Called when the user clicks the action button */
  onAction: (selectedSheets: string[], sheetOrder: string[]) => Promise<void>
  /** Disable the trigger button entirely */
  disabled?: boolean
  /** Whether the action is currently in progress (disables UI) */
  busy?: boolean
  /** Fill the row (mobile header): the trigger splits its button row via the
   * shared MOBILE_FULL_BUTTON override. */
  fullWidth?: boolean
  /** Which edge of the trigger the 320px panel hangs from. A trigger on the
   * left half of a phone row must anchor left or the panel leaves the
   * viewport; the desktop header's right-edge cluster keeps the default. */
  panelAlign?: "left" | "right"
}

export default function SheetSelectionDropdown({
  triggerLabel,
  leadingIcon,
  busyLabel,
  actionLabel,
  onAction,
  disabled = false,
  busy = false,
  fullWidth = false,
  panelAlign = "right",
}: Props) {
  const { preferences, savePreferences } = usePhotoList()

  const [open, setOpen] = useState(false)
  const [selectedSheets, setSelectedSheets] = useState<Set<string>>(new Set(ALL_SHEET_LABELS))
  const [sheetOrder, setSheetOrder] = useState<string[]>([...ALL_SHEET_LABELS])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const selectAllRef = useRef<HTMLInputElement>(null)

  // Sync from preferences when opening
  useEffect(() => {
    if (!open) return
    if (preferences) {
      setSelectedSheets(new Set(preferences.selected_sheets))
      setSheetOrder(preferences.sheet_order.length > 0 ? [...preferences.sheet_order] : [...ALL_SHEET_LABELS])
    } else {
      setSelectedSheets(new Set(ALL_SHEET_LABELS))
      setSheetOrder([...ALL_SHEET_LABELS])
    }
    setError(null)
    setSaveMessage(null)
  }, [open, preferences])

  // Select All tri-state
  const selectAllChecked = selectedSheets.size === ALL_SHEET_LABELS.length
  const selectAllIndeterminate = selectedSheets.size > 0 && selectedSheets.size < ALL_SHEET_LABELS.length

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectAllIndeterminate
    }
  }, [selectAllIndeterminate])

  const handleSelectAll = useCallback(() => {
    if (selectAllChecked) {
      setSelectedSheets(new Set())
    } else {
      setSelectedSheets(new Set(ALL_SHEET_LABELS))
    }
  }, [selectAllChecked])

  // Click outside close
  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  // Escape close
  useEffect(() => {
    if (!open) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [open])

  const toggleSheet = useCallback((label: string) => {
    setSelectedSheets((prev) => {
      const next = new Set(prev)
      if (next.has(label)) {
        next.delete(label)
      } else {
        next.add(label)
      }
      return next
    })
  }, [])

  const moveSheet = useCallback((index: number, direction: -1 | 1) => {
    setSheetOrder((prev) => {
      const next = [...prev]
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= next.length) return prev
      ;[next[index], next[targetIndex]] = [next[targetIndex], next[index]]
      return next
    })
  }, [])

  const handleSaveDefaults = useCallback(async () => {
    setSaving(true)
    setSaveMessage(null)
    try {
      await savePreferences({
        selected_sheets: sheetOrder.filter((s) => selectedSheets.has(s)),
        sheet_order: sheetOrder,
      })
      setSaveMessage("Saved as default")
      setTimeout(() => setSaveMessage(null), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save preferences")
    } finally {
      setSaving(false)
    }
  }, [savePreferences, sheetOrder, selectedSheets])

  const handleAction = useCallback(async () => {
    setError(null)
    try {
      const selected = sheetOrder.filter((s) => selectedSheets.has(s))
      await onAction(selected, sheetOrder)
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed")
    }
  }, [onAction, sheetOrder, selectedSheets])

  const allBusy = saving || busy
  const actionDisabled = disabled || busy || selectedSheets.size === 0

  const buttonStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: THEME.spacing.xs,
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    fontSize: 14,
    fontWeight: 500,
    borderRadius: THEME.borderRadius.md,
    border: "none",
    backgroundColor: THEME.colors.navy,
    color: THEME.colors.white,
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: THEME.fonts.primary,
    transition: "opacity 0.15s",
    whiteSpace: "nowrap",
    // flex: initial — the container is the flex-row sibling, not the button,
    // so the button just fills the container the override sized.
    ...(fullWidth && { ...MOBILE_FULL_BUTTON, flex: "initial", width: "100%" }),
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        ...(fullWidth
          ? { display: "block", flex: "1 1 0", minWidth: 0 }
          : { display: "inline-block" }),
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={disabled}
        style={buttonStyle}
      >
        {leadingIcon}
        {busy ? busyLabel : triggerLabel}
        <ChevronDown size={14} strokeWidth={2} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            ...(panelAlign === "left" ? { left: 0 } : { right: 0 }),
            // border-box so 320 is the panel's real outer width — content-box
            // let the padding push it 4px past a 390px viewport.
            width: 320,
            boxSizing: "border-box",
            // Never wider than the viewport minus the page gutters — guards
            // the 320px panel on screens narrower than ~390px.
            maxWidth: "calc(100vw - 48px)",
            backgroundColor: THEME.colors.neutral.light.surface,
            border: `1px solid ${THEME.colors.silver}`,
            borderRadius: THEME.borderRadius.md,
            zIndex: 100,
            padding: THEME.spacing.md,
            fontFamily: THEME.fonts.primary,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              marginBottom: THEME.spacing.sm,
              color: THEME.colors.neutral.light.textPrimary,
            }}
          >
            Select sheets and order:
          </div>

          {/* Select All */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: THEME.colors.neutral.light.textPrimary,
              cursor: allBusy ? "default" : "pointer",
              marginBottom: THEME.spacing.sm,
              paddingLeft: THEME.spacing.sm,
            }}
          >
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={selectAllChecked}
              onChange={handleSelectAll}
              disabled={allBusy}
              style={{ margin: 0, cursor: allBusy ? "default" : "pointer" }}
            />
            Select All
          </label>

          {/* Scrollable sheet list */}
          <div
            style={{
              maxHeight: 228,
              overflowY: "auto",
              border: `1px solid ${THEME.colors.silver}`,
              borderRadius: THEME.borderRadius.sm,
              marginBottom: THEME.spacing.sm,
            }}
          >
            {sheetOrder.map((label, index) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: `4px ${THEME.spacing.sm}`,
                  borderBottom:
                    index < sheetOrder.length - 1
                      ? `1px solid ${THEME.colors.neutral.light.background}`
                      : "none",
                  backgroundColor:
                    index % 2 === 0
                      ? THEME.colors.neutral.light.surface
                      : THEME.colors.neutral.light.background,
                  fontSize: 13,
                }}
              >
                {/* Reorder arrows */}
                <button
                  type="button"
                  disabled={index === 0 || allBusy}
                  onClick={() => moveSheet(index, -1)}
                  title="Move up"
                  style={{
                    background: "none",
                    border: "none",
                    cursor:
                      index === 0 || allBusy ? "default" : "pointer",
                    color:
                      index === 0 || allBusy
                        ? THEME.colors.silver
                        : THEME.colors.neutral.light.textSecondary,
                    padding: 0,
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <ChevronUp size={12} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  disabled={index === sheetOrder.length - 1 || allBusy}
                  onClick={() => moveSheet(index, 1)}
                  title="Move down"
                  style={{
                    background: "none",
                    border: "none",
                    cursor:
                      index === sheetOrder.length - 1 || allBusy
                        ? "default"
                        : "pointer",
                    color:
                      index === sheetOrder.length - 1 || allBusy
                        ? THEME.colors.silver
                        : THEME.colors.neutral.light.textSecondary,
                    padding: 0,
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <ChevronDown size={12} strokeWidth={2} />
                </button>

                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={selectedSheets.has(label)}
                  onChange={() => toggleSheet(label)}
                  disabled={allBusy}
                  style={{ margin: 0, cursor: allBusy ? "default" : "pointer" }}
                />

                {/* Label */}
                <span
                  style={{
                    flex: 1,
                    color: selectedSheets.has(label)
                      ? THEME.colors.neutral.light.textPrimary
                      : THEME.colors.neutral.light.textSecondary,
                  }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* Save as default */}
          <div style={{ marginBottom: THEME.spacing.sm }}>
            <button
              type="button"
              onClick={handleSaveDefaults}
              disabled={allBusy}
              style={{
                background: "none",
                border: "none",
                color: allBusy
                  ? THEME.colors.neutral.light.textSecondary
                  : THEME.colors.navy,
                fontSize: 12,
                fontWeight: 500,
                cursor: allBusy ? "default" : "pointer",
                padding: 0,
                textDecoration: "underline",
                fontFamily: THEME.fonts.primary,
              }}
            >
              {saving ? "Saving..." : "Save as my default"}
            </button>
            {saveMessage && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 12,
                  color: THEME.colors.semantic.success,
                }}
              >
                {saveMessage}
              </span>
            )}
          </div>

          {/* Error */}
          {error && (
            <div
              style={{
                fontSize: 12,
                color: THEME.colors.semantic.danger,
                marginBottom: THEME.spacing.sm,
              }}
            >
              {error}
            </div>
          )}

          {/* Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: THEME.spacing.sm,
            }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={allBusy}
              style={{
                padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
                fontSize: 13,
                fontWeight: 500,
                borderRadius: THEME.borderRadius.md,
                border: `1px solid ${THEME.colors.silver}`,
                backgroundColor: THEME.colors.neutral.light.surface,
                color: THEME.colors.neutral.light.textPrimary,
                fontFamily: THEME.fonts.primary,
                cursor: allBusy ? "default" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAction}
              disabled={actionDisabled}
              style={{
                padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
                fontSize: 14,
                fontWeight: 500,
                borderRadius: THEME.borderRadius.md,
                border: "none",
                backgroundColor: THEME.colors.navy,
                color: THEME.colors.white,
                fontFamily: THEME.fonts.primary,
                opacity: actionDisabled ? 0.5 : 1,
                cursor: actionDisabled ? "not-allowed" : "pointer",
                transition: "opacity 0.15s",
              }}
            >
              {busy ? busyLabel : actionLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
